import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { signupRequest, clearError } from '../../redux/actions/authActions';
import { ThunderboltOutlined, LockOutlined, LineChartOutlined, UserOutlined } from '@ant-design/icons';
import './Auth.css';

function Signup() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error, isAuthenticated } = useSelector(state => state.auth);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    // Redirect if already authenticated
    if (isAuthenticated) {
      navigate('/purchase-order');
    }
    // Clear any existing errors when component mounts
    dispatch(clearError());
  }, [isAuthenticated, navigate, dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // Clear API error when user starts typing
    if (error) {
      dispatch(clearError());
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validateForm();

    if (Object.keys(newErrors).length === 0) {
      dispatch(signupRequest(formData));
    } else {
      setValidationErrors(newErrors);
    }
  };

  return (
    <div className="modern-auth-container">
      {/* Left Side - Branding */}
      <div className="auth-brand-section">
        <div className="brand-content">
          <div className="brand-logo" onClick={() => navigate('/')}>
            <h1>Kalapi Prints</h1>
            <p className="brand-subtitle">Purchase Order Management</p>
          </div>

          <div className="brand-features">
            <div className="brand-feature-item">
              <div className="feature-icon-circle"><ThunderboltOutlined /></div>
              <div>
                <h3>Quick Setup</h3>
                <p>Get started in minutes with our easy setup process</p>
              </div>
            </div>

            <div className="brand-feature-item">
              <div className="feature-icon-circle"><LockOutlined /></div>
              <div>
                <h3>Secure & Safe</h3>
                <p>Your data is protected with enterprise-grade security</p>
              </div>
            </div>

            <div className="brand-feature-item">
              <div className="feature-icon-circle"><LineChartOutlined /></div>
              <div>
                <h3>Grow Your Business</h3>
                <p>Scale efficiently with powerful management tools</p>
              </div>
            </div>
          </div>

          <div className="brand-footer">
            <p>Join businesses streamlining their operations</p>
          </div>
        </div>
      </div>

      {/* Right Side - Signup Form */}
      <div className="auth-form-section">
        <div className="auth-form-container">
          {/* Back to Home Link */}
          <button className="back-home-link" onClick={() => navigate('/')}>
            ← Back to Home
          </button>

          <div className="auth-header">
            <h2>Create Account</h2>
            <p className="auth-subtitle">Start managing your purchase orders today</p>
          </div>

          {error && (
            <div className="modern-error-alert">
              <span className="error-icon">⚠</span>
              <div className="error-content">
                <strong>Signup Failed</strong>
                <p>{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="modern-auth-form">
            <div className="modern-form-group">
              <label htmlFor="name">Full Name</label>
              <div className="input-wrapper">
                <span className="input-icon"><UserOutlined /></span>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={handleChange}
                  className={validationErrors.name ? 'error' : ''}
                  disabled={loading}
                  autoComplete="name"
                />
              </div>
              {validationErrors.name && (
                <span className="modern-error-message">{validationErrors.name}</span>
              )}
            </div>

            <div className="modern-form-group">
              <label htmlFor="email">Email Address</label>
              <div className="input-wrapper">
                <span className="input-icon">✉</span>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  className={validationErrors.email ? 'error' : ''}
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
              {validationErrors.email && (
                <span className="modern-error-message">{validationErrors.email}</span>
              )}
            </div>

            <div className="modern-form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <span className="input-icon"><LockOutlined /></span>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  placeholder="Create a password (min. 6 characters)"
                  value={formData.password}
                  onChange={handleChange}
                  className={validationErrors.password ? 'error' : ''}
                  disabled={loading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex="-1"
                  disabled={loading}
                >
                  {showPassword ? '👁' : '👁‍🗨'}
                </button>
              </div>
              {validationErrors.password && (
                <span className="modern-error-message">{validationErrors.password}</span>
              )}
            </div>

            <div className="modern-form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="input-wrapper">
                <span className="input-icon"><LockOutlined /></span>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  placeholder="Re-enter your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={validationErrors.confirmPassword ? 'error' : ''}
                  disabled={loading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex="-1"
                  disabled={loading}
                >
                  {showConfirmPassword ? '👁' : '👁‍🗨'}
                </button>
              </div>
              {validationErrors.confirmPassword && (
                <span className="modern-error-message">{validationErrors.confirmPassword}</span>
              )}
            </div>

            <button
              type="submit"
              className="modern-auth-button"
              disabled={loading}
            >
              {loading ? (
                <span className="button-loading">
                  <span className="spinner"></span>
                  Creating account...
                </span>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <div className="auth-footer-links">
            <p>
              Already have an account?{' '}
              <Link to="/login" className="signup-link">
                Sign In
              </Link>
            </p>
          </div>

          <div className="auth-divider">
            <span>Secure Registration</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup;